import { useEffect, useState } from 'react';
import { TaskManual } from './TaskManual';
import { ManualView } from './ManualView';
import { TaskTimeField } from './TaskTimeField';
import { TaskDetailActions } from './TaskDetailActions';
import { hasManual } from '@/tasks/sanitize-html';
import { formatYearMonth, weekdayLabel } from '@/tasks/calendar-range';
import { STATUSES, type Task } from '@/tasks/task';
import { MONTHLY_KIND } from '@/fmt/generate-tasks';
import {
  composeTaskTitle,
  splitTaskTitle,
  type TitleOptions,
  type TitleParts,
} from '@/tasks/task-title';
import { phaseColor } from '@/pages/MonthlyAccounting';
import { businessColor } from './BusinessLabel';
import { KindLabel } from './KindLabel';
import { KindPicker, PillSelect, pastel } from './PillSelect';
import { STATUS_CLASS } from './StatusBadge';

/** @graph-connects none */
function formatFullDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return `${String(year)}年${String(month)}月${String(day)}日（${weekdayLabel(iso)}）`;
}

/**
 * 何月分かに選べる範囲。いま選んでいる月の前後 3 か月ぶん
 * @graph-connects none
 */
const TARGET_MONTH_RANGE = 3;

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 何月分かの選択肢を、いま選んでいる月を中心に前後 3 か月ぶん新しい順で返す。年と月を分けて 2 回選ばせない
 * @graph-connects none
 */
export function targetMonthOptions(current: string): string[] {
  const [year, month] = current.split('-').map(Number);
  return Array.from({ length: TARGET_MONTH_RANGE * 2 + 1 }, (_, index) => {
    const shifted = new Date(
      Date.UTC(year ?? 1970, (month ?? 1) - 1 + (TARGET_MONTH_RANGE - index), 1),
    );
    return `${String(shifted.getUTCFullYear())}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 何月分かが決まっていないときの既定を返す。締めるのは翌月なので、対応日の前月にする
 * @graph-connects none
 */
export function defaultTargetMonth(dueDate: string): string {
  const [year, month] = dueDate.split('-').map(Number);
  const previous = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 2, 1));
  return `${String(previous.getUTCFullYear())}-${String(previous.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** @graph-connects none */
const FIELD_CLASS =
  'w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2.5 py-1.5 text-sm text-[var(--color-text-primary)]';

/**
 * 添えものの項目（担当・種別・何月分）。
 *
 * ふだんは枠を見せず、触れたときだけ出す。同じ見た目のプルダウンが並ぶと、
 * どれが主役か分からなくなるため
 * @graph-connects none
 */
const QUIET_FIELD_CLASS =
  'w-full cursor-pointer rounded border border-transparent bg-transparent px-2.5 py-1.5 text-sm text-[var(--color-text-primary)] hover:border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-surface)] focus:border-[var(--color-border-subtle)] focus:bg-[var(--color-bg-surface)]';

/**
 * TaskDetail の props。
 */
export interface TaskDetailProps {
  /** 表示するタスク */
  task: Task;
  /** 担当の入力候補 */
  assignees?: readonly string[];
  /** 種別の入力候補 */
  kinds?: readonly string[];
  /**
   * 種別ごとの区分（大分類・事業・中分類）の選択肢。月次経理・決算のように、ここに選択肢が
   * ある種別のタスクだけ、区分をプルダウンで選び直せる。それ以外の種別では区分を出さない
   */
  titleOptionsByKind?: Readonly<Record<string, TitleOptions>>;
  /**
   * 元の FMT 項目のマニュアル。タスクにマニュアルが写っていないとき（マニュアルを書く前に
   * 起こしたタスクなど）に代わりに出す
   */
  fmtManual?: string;
  /** 保存するときに呼ばれる。渡さないと閲覧のみになる */
  onSave?: (task: Task) => void;
  /** 消すときに呼ばれる。渡さなければ削除を出さない（作る前の新しいタスクにも出さない） */
  onDelete?: () => void;
  /**
   * 写しを作るときに呼ばれる。直しかけの内容は先に保存してから渡す。渡さなければ複製を出さない
   * （作る前の新しいタスクにも出さない）
   */
  onDuplicate?: (task: Task) => void;
  /** 閉じるときに呼ばれる */
  onClose: () => void;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 選んだタスクの詳細を画面中央のポップアップで表示し、状況・対応日・担当・種別を直せるようにする。月次経理を選んだときは何月分かも選べる。Esc と背景クリックで閉じる
 * @graph-connects defaultTargetMonth [calls] 何月分かの既定を決める
 * @graph-connects TaskTimeField [calls] 時刻を出し、直せるときは入れさせる
 * @graph-connects TaskDetailActions [calls] 削除・複製・取りやめる・保存を出す
 * @graph-connects TaskManual [calls] マニュアルを大きく開く
 * @graph-connects ManualView [calls] マニュアルを詳細の中に出す
 * @graph-connects hasManual [calls] マニュアルが書いてあるかを調べる
 * @graph-connects splitTaskTitle [calls] 内容を大分類・事業・中分類・内容に分けて見せる
 * @graph-connects businessColor [calls] 事業の色を引く
 * @graph-connects composeTaskTitle [calls] 選び直した区分から内容を組み立てる
 * @graph-connects PillSelect [calls] 区分をピルのプルダウンで選ばせる
 * @graph-connects KindPicker [calls] 作るときに種類を選ばせる
 * @graph-connects pastel [calls] ピルの色をパステルにする
 */
export function TaskDetail({
  task,
  assignees = [],
  kinds = [],
  titleOptionsByKind = {},
  fmtManual,
  onSave,
  onDelete,
  onDuplicate,
  onClose,
}: TaskDetailProps) {
  const [draft, setDraft] = useState<Task>(task);
  // マニュアルを開いているか。詳細を閉じずに手順だけ重ねて読めるようにする
  const [showingManual, setShowingManual] = useState(false);

  // Esc で閉じる。パネルを開いたまま操作を続けられるほうが一覧との往復が少ない
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const update = (patch: Partial<Task>) => {
    setDraft({ ...draft, ...patch });
  };
  const editable = onSave !== undefined;
  // 設定にある種別に、いま入っている値を足す。設定から消された種別でも選び直せるようにする
  const kindOptions = [...new Set([...kinds, draft.kind].filter((name) => name !== ''))].sort(
    (a, b) => a.localeCompare(b, 'ja'),
  );
  // 設定にある担当に、いま入っている人を足す。設定から消された人でも選び直せるようにする
  const assigneeOptions = [
    ...new Set([...assignees, draft.assignee].filter((name) => name !== '')),
  ].sort((a, b) => a.localeCompare(b, 'ja'));
  const changed = JSON.stringify(draft) !== JSON.stringify(task);
  // 出すマニュアル。タスクに写っていなければ元の FMT 項目のものを出す
  const manual = hasManual(draft.manual)
    ? draft.manual
    : hasManual(fmtManual)
      ? fmtManual
      : undefined;
  // FMT から起こしたタスクは区分ごとに分けて見せる（手で作ったものは null のまま 1 行で出す）
  const titleParts = splitTaskTitle(draft.title);
  // 区分を選び直すときの今の値。内容から読み戻すと、大分類だけ選んだ途中の形（区分が足りない）を
  // 区分と見なせないので、選んだ値は別に持つ。区分の無いタスクは、作業の名前だけを持つ形にする
  const [parts, setParts] = useState<TitleParts>(
    () =>
      splitTaskTitle(task.title) ?? {
        category: '',
        business: '',
        middleCategory: '',
        name: task.title,
      },
  );
  // 区分を選べるのは、直せて、その種別に選択肢があるとき（月次経理・決算）だけ
  const titleOptions = titleOptionsByKind[draft.kind];
  const choosable = editable && titleOptions !== undefined;
  // 直せるときは、作るときも直すときも、まず種別をピルで選ばせ、その種別の区分を出す
  const choosingKind = editable;
  /** 区分か作業の名前を差し替え、内容を組み立て直す */
  const updatePart = (patch: Partial<TitleParts>) => {
    const next = { ...parts, ...patch };
    const businesses = titleOptions?.businesses ?? [];
    const middles = titleOptions?.middleByCategory[next.category] ?? [];
    if (next.category === '') {
      // 大分類を外したら事業・中分類も外す
      next.business = '';
      next.middleCategory = '';
    } else {
      // 大分類を選んだら事業は必須。空のまま組み立てると区分の位置がずれるので、共通（無ければ先頭）を入れる
      if (next.business === '') {
        next.business = businesses.includes('共通') ? '共通' : (businesses[0] ?? '共通');
      }
      // 大分類が変わったら、その大分類に無い中分類は外す
      if (patch.category !== undefined && !middles.includes(next.middleCategory)) {
        next.middleCategory = '';
      }
    }
    setParts(next);
    update({ title: composeTaskTitle(next) });
  };
  /**
   * 種別を変える。区分の選択肢は種別ごとに違うので、種別が変わったら区分は外し、作業の名前
   * だけ残す。月次経理でなくなったら、何月分かは意味を持たないので外す
   */
  const changeKind = (kind: string) => {
    // 元の種別に戻したときは、元の区分も戻す（押し間違えで区分が消えたままにならないように）
    const original = kind === task.kind ? splitTaskTitle(task.title) : null;
    const next = original ?? { category: '', business: '', middleCategory: '', name: parts.name };
    setParts(next);
    setDraft({
      ...draft,
      kind,
      title: composeTaskTitle(next),
      targetMonth:
        kind === MONTHLY_KIND
          ? (draft.targetMonth ?? defaultTargetMonth(draft.dueDate))
          : undefined,
    });
  };
  // 選択肢に今の値を足す。選択肢に無い値でも選び直せるようにする
  const withCurrent = (values: readonly string[], current: string) =>
    [...new Set([...values, current].filter((value) => value !== ''))].sort((a, b) =>
      a.localeCompare(b, 'ja'),
    );

  return (
    <>
      {/* 背景。クリックで閉じる。暗くしすぎると一覧が読めなくなるので薄くする */}
      <button
        type="button"
        aria-label="詳細を閉じる"
        onClick={onClose}
        className="fixed inset-0 z-10 cursor-default bg-[var(--color-text-primary)]/20"
      />
      {/* 画面中央のポップアップ。クリックした一覧の近くに出すより、視線の落ち着く
          中央に固定したほうが読みやすい */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="タスクの詳細"
        className="fixed top-1/2 left-1/2 z-20 flex max-h-[85vh] w-[min(34rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-6 py-5 shadow-[0_8px_32px_var(--color-shadow)]"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <span className="flex flex-wrap items-center gap-2 text-xs">
            {/* 直せるときは下のピルで種類を選ぶので、ここには出さない（同じことが 2 か所に並ぶ） */}
            {editable ? null : <KindLabel kind={draft.kind} />}
            {/* どの月の締めのための作業かは、対応日だけでは分からない（月をまたいで
                対応することもある）ので、種別の隣に出す */}
            {/* 何月分かは下の欄で選ぶ。ここにも出すと同じことが 2 か所に並ぶので、
                直せないとき（読むだけのとき）にだけ添える */}
            {editable || draft.targetMonth === undefined ? null : (
              <span className="rounded bg-[var(--color-kind-monthly-bg)] px-2 py-0.5 text-sm font-semibold text-[var(--color-kind-monthly-text)]">
                {formatYearMonth(draft.targetMonth)}分
              </span>
            )}
          </span>
          <button
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="-mt-1 shrink-0 px-1 text-lg leading-none text-[var(--color-text-muted)]"
          >
            ×
          </button>
        </div>

        {/* 区分（大分類・事業・中分類）。FMT から起こしたタスクは内容に畳み込まれているので、
            ポップなピルに分けて見せる。直せるときはピルがそのままプルダウンになり、
            カレンダーから新しく作るときも選べる */}
        {/* まず種別を選ぶ。月次経理・決算なら、その下にそれぞれの区分が出る */}
        {choosingKind ? (
          <KindPicker
            value={draft.kind}
            kinds={kindOptions}
            onChange={(kind) => {
              changeKind(kind);
            }}
          />
        ) : null}

        {choosable ? (
          <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs font-semibold">
            <PillSelect
              label="大分類"
              value={parts.category}
              emptyLabel="大分類なし"
              options={withCurrent(titleOptions.categories, parts.category)}
              color={parts.category === '' ? undefined : phaseColor(parts.category).color}
              onChange={(category) => {
                updatePart({ category });
              }}
            />
            {parts.category === '' ? null : (
              <>
                <PillSelect
                  label="事業"
                  value={parts.business}
                  emptyLabel="事業なし"
                  allowEmpty={false}
                  options={withCurrent(titleOptions.businesses, parts.business)}
                  color={parts.business === '' ? undefined : businessColor(parts.business).color}
                  onChange={(business) => {
                    updatePart({ business });
                  }}
                />
                <PillSelect
                  label="中分類"
                  value={parts.middleCategory}
                  emptyLabel="中分類なし"
                  options={withCurrent(
                    titleOptions.middleByCategory[parts.category] ?? [],
                    parts.middleCategory,
                  )}
                  color={parts.middleCategory === '' ? undefined : 'var(--color-pill-middle)'}
                  onChange={(middleCategory) => {
                    updatePart({ middleCategory });
                  }}
                />
              </>
            )}
          </div>
        ) : titleParts === null ? null : (
          <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs font-semibold">
            <span
              className="rounded-full px-3 py-1 text-[var(--color-pill-text)] shadow-sm"
              style={{ backgroundColor: pastel(phaseColor(titleParts.category).color) }}
            >
              {titleParts.category}
            </span>
            <span
              className="rounded-full px-3 py-1 text-[var(--color-pill-text)] shadow-sm"
              style={{ backgroundColor: pastel(businessColor(titleParts.business).color) }}
            >
              {titleParts.business}
            </span>
            {titleParts.middleCategory === '' ? null : (
              <span className="rounded-full bg-[color-mix(in_srgb,var(--color-pill-middle)_55%,white)] px-3 py-1 text-[var(--color-pill-text)] shadow-sm">
                {titleParts.middleCategory}
              </span>
            )}
          </div>
        )}

        {/* 引いて作ったばかりの予定は内容が空なので、そのまま書けるようにする。
            すでに内容のあるタスクは見出しとして読ませる */}
        {editable && task.title === '' ? (
          <input
            type="text"
            autoFocus
            aria-label="内容"
            placeholder="内容を入れてください"
            value={parts.name}
            onChange={(event) => {
              updatePart({ name: event.target.value });
            }}
            className="m-0 w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-base font-semibold text-[var(--color-text-primary)]"
          />
        ) : (
          <h2
            className={
              draft.status === '対応済'
                ? 'm-0 text-base leading-relaxed font-semibold text-[var(--color-text-muted)] line-through'
                : 'm-0 text-base leading-relaxed font-semibold text-[var(--color-text-primary)]'
            }
          >
            {parts.name}
          </h2>
        )}

        <dl className="mt-5 mb-0 grid grid-cols-[5rem_1fr] items-center gap-y-3 text-sm">
          <dt className="text-[var(--color-text-muted)]">状況</dt>
          <dd className="m-0">
            {/* 状況はいちばんよく触るので、押せば変わる形にする。プルダウンを並べると
                どれが主役か分からなくなり、開く手間もかかる */}
            <div role="radiogroup" aria-label="状況" className="flex gap-1">
              {STATUSES.map((status) => (
                <button
                  key={status}
                  type="button"
                  role="radio"
                  aria-checked={draft.status === status}
                  aria-label={status}
                  disabled={!editable}
                  onClick={() => {
                    update({ status });
                  }}
                  className={
                    draft.status === status
                      ? `flex-1 cursor-pointer rounded border px-2 py-1.5 text-sm font-semibold ${STATUS_CLASS[status]}`
                      : 'flex-1 cursor-pointer rounded border border-transparent px-2 py-1.5 text-sm text-[var(--color-text-muted)] hover:bg-[var(--color-bg-subtle)]'
                  }
                >
                  {status}
                </button>
              ))}
            </div>
          </dd>

          <dt className="text-[var(--color-text-muted)]">対応日</dt>
          <dd className="m-0">
            <input
              type="date"
              aria-label="対応日"
              value={draft.dueDate}
              onChange={(event) => {
                update({ dueDate: event.target.value });
              }}
              className={`${FIELD_CLASS} tabular-nums`}
            />
            <span className="mt-1 block text-xs text-[var(--color-text-muted)]">
              {formatFullDate(draft.dueDate)}
            </span>
          </dd>

          {/* 時刻は常に出す。時刻の無いタスクは「終日」とし、直せるときはその場で入れられる */}
          <dt className="text-[var(--color-text-muted)]">時刻</dt>
          <dd className="m-0">
            <TaskTimeField
              task={draft}
              editable={editable}
              fieldClass={FIELD_CLASS}
              onChange={(patch) => {
                update(patch);
              }}
            />
          </dd>

          <dt className="text-[var(--color-text-muted)]">担当</dt>
          <dd className="m-0">
            <select
              aria-label="担当"
              value={draft.assignee}
              onChange={(event) => {
                update({ assignee: event.target.value });
              }}
              className={QUIET_FIELD_CLASS}
            >
              {/* 決まっていないタスクもあるので、空も選べるようにする */}
              <option value="">未定</option>
              {assigneeOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </dd>

          {/* 種別は、直せるときは上のピルで選び、読むだけのときは上のラベルで見せる */}

          {/* 何月分かは月次経理だけが持つ。ほかの種別では出さない */}
          {draft.kind === MONTHLY_KIND ? (
            <>
              <dt className="text-[var(--color-text-muted)]">何月分</dt>
              <dd className="m-0">
                {/* 年と月を分けると 2 回選ぶことになる。「2026年8月」で 1 回にする */}
                <select
                  aria-label="何月分"
                  value={draft.targetMonth ?? defaultTargetMonth(draft.dueDate)}
                  onChange={(event) => {
                    update({ targetMonth: event.target.value });
                  }}
                  className={QUIET_FIELD_CLASS}
                >
                  {targetMonthOptions(draft.targetMonth ?? defaultTargetMonth(draft.dueDate)).map(
                    (month) => (
                      <option key={month} value={month}>
                        {formatYearMonth(month)}
                      </option>
                    ),
                  )}
                </select>
              </dd>
            </>
          ) : null}

          {draft.estimatedHours === undefined || draft.estimatedHours === null ? null : (
            <>
              <dt className="text-[var(--color-text-muted)]">想定時間</dt>
              <dd className="m-0 tabular-nums text-[var(--color-text-primary)]">
                {draft.estimatedHours.toFixed(2)} 時間
              </dd>
            </>
          )}
        </dl>

        {/* マニュアルは FMT 側で書くので、ここからは読むだけ。押さずに読めるよう詳細の中に
            そのまま出し、長いときは「大きく開く」で別の画面に広げる */}
        {manual === undefined ? null : (
          <section aria-label="マニュアル" className="mt-5">
            <div className="mb-1.5 flex items-center justify-between">
              <h3 className="m-0 text-sm font-medium text-[var(--color-text-muted)]">マニュアル</h3>
              <button
                type="button"
                onClick={() => {
                  setShowingManual(true);
                }}
                className="cursor-pointer rounded px-2 py-0.5 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]"
              >
                大きく開く
              </button>
            </div>
            <ManualView
              manual={manual}
              className="max-h-64 overflow-y-auto rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-base)] px-4 py-3"
            />
          </section>
        )}

        {editable ? (
          <TaskDetailActions
            canDelete={onDelete !== undefined && task.id !== ''}
            changed={changed}
            onDelete={() => {
              onDelete?.();
            }}
            onDuplicate={
              onDuplicate === undefined || task.id === ''
                ? undefined
                : () => {
                    // 直しかけ（例: 今日のぶんを対応中にした）を失わないよう、先に保存する
                    if (changed) {
                      onSave(draft);
                    }
                    onDuplicate(draft);
                  }
            }
            onCancel={onClose}
            onSave={() => {
              onSave(draft);
            }}
          />
        ) : null}
      </aside>

      {showingManual && manual !== undefined ? (
        <TaskManual
          title={draft.title}
          manual={manual}
          onClose={() => {
            setShowingManual(false);
          }}
        />
      ) : null}
    </>
  );
}
